<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ArrowLeft, Ban, Plus, Save, Sparkles } from '@lucide/vue'
import {
  Badge, Button, EmptyState, FormField, Input, PageHeader, Section, Select, Spinner, Table, Textarea, useToast,
} from '@drama/ui'

import {
  getEntity, getHardFacts, listEntityAssets, listEntityRelationships,
  runEntityDeepResearch, upsertEntityAsset, upsertEntityRelationship, updateHardFacts, withdrawEntityAsset,
} from '../../api/entity-library.js'
import { formatDate } from '../intent-video/format.js'

const props = defineProps({ entityId: { type: String, required: true } })

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

const loading = ref(true)
const error = ref('')
const entity = ref(null)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [entityData, hardFactsData, assetsData, relationshipsData] = await Promise.all([
      getEntity(props.entityId),
      getHardFacts(props.entityId),
      listEntityAssets(props.entityId),
      listEntityRelationships(props.entityId),
    ])
    entity.value = entityData.entity
    hardFactsText.value = JSON.stringify(hardFactsData.hard_facts?.facts ?? {}, null, 2)
    assets.value = assetsData.assets
    relationships.value = relationshipsData.relationships
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

onMounted(load)

function back() {
  router.push({ name: 'entity-library' })
}

// ------------------------------------------------------------------ 世界观硬事实（hard_facts）

const hardFactsText = ref('{}')
const savingFacts = ref(false)
const factsError = ref('')

async function saveHardFacts() {
  let parsed
  try {
    parsed = JSON.parse(hardFactsText.value)
  } catch {
    factsError.value = t('visualGameAdmin.entityLibrary.detail.factsInvalidJson')
    return
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    factsError.value = t('visualGameAdmin.entityLibrary.detail.factsInvalidJson')
    return
  }
  factsError.value = ''
  savingFacts.value = true
  try {
    await updateHardFacts(props.entityId, parsed)
    toast.success(t('visualGameAdmin.entityLibrary.detail.factsSaved'))
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    savingFacts.value = false
  }
}

// ------------------------------------------------------------------ 资产（entity_asset）

const assets = ref([])
const withdrawingId = ref('')
const assetColumns = computed(() => [
  { key: 'asset_type', label: t('visualGameAdmin.entityLibrary.detail.assets.type'), width: '80px' },
  { key: 'pack_id', label: t('visualGameAdmin.entityLibrary.detail.assets.pack'), width: '160px', mono: true },
  { key: 'role', label: t('visualGameAdmin.entityLibrary.detail.assets.role'), width: '140px', mono: true },
  { key: 'status', label: t('visualGameAdmin.entityLibrary.detail.assets.status'), width: '100px' },
  { key: 'ref', label: t('visualGameAdmin.entityLibrary.detail.assets.ref'), width: '220px', mono: true },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '100px', align: 'right', fixed: 'right' },
])

async function withdraw(row) {
  withdrawingId.value = row.id
  try {
    const { asset } = await withdrawEntityAsset(props.entityId, row.id)
    const index = assets.value.findIndex(item => item.id === asset.id)
    if (index !== -1) assets.value[index] = asset
    toast.success(t('visualGameAdmin.entityLibrary.detail.assets.withdrawn'))
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    withdrawingId.value = ''
  }
}

const assetForm = reactive({ asset_type: 'image', pack_id: '', media_asset_id: '', prompt_id: '', role: '' })
const addingAsset = ref(false)
const assetFormValid = computed(() => {
  if (assetForm.role.trim() === '') return false
  return assetForm.asset_type === 'prompt' ? assetForm.prompt_id.trim() !== '' : assetForm.media_asset_id.trim() !== ''
})

async function addAsset() {
  addingAsset.value = true
  try {
    const { asset } = await upsertEntityAsset(props.entityId, {
      asset_type: assetForm.asset_type,
      pack_id: assetForm.pack_id.trim() || null,
      media_asset_id: assetForm.asset_type === 'prompt' ? null : assetForm.media_asset_id.trim(),
      prompt_id: assetForm.asset_type === 'prompt' ? assetForm.prompt_id.trim() : null,
      role: assetForm.role.trim(),
    })
    const index = assets.value.findIndex(item => item.id === asset.id)
    if (index !== -1) assets.value[index] = asset
    else assets.value.push(asset)
    assetForm.pack_id = ''
    assetForm.media_asset_id = ''
    assetForm.prompt_id = ''
    assetForm.role = ''
    toast.success(t('visualGameAdmin.entityLibrary.detail.assets.saved'))
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    addingAsset.value = false
  }
}

// ------------------------------------------------------------------ 关系（entity_relationship）

const relationships = ref([])
const relationshipColumns = computed(() => [
  { key: 'direction', label: t('visualGameAdmin.entityLibrary.detail.relationships.direction'), width: '90px' },
  { key: 'other_canonical_name', label: t('visualGameAdmin.entityLibrary.detail.relationships.other'), width: '140px' },
  { key: 'address', label: t('visualGameAdmin.entityLibrary.detail.relationships.address'), width: '140px' },
  { key: 'notes', label: t('visualGameAdmin.entityLibrary.detail.relationships.notes'), width: '320px' },
])

const relForm = reactive({ to_entity_id: '', address: '', notes: '' })
const savingRel = ref(false)

async function addRelationship() {
  if (relForm.to_entity_id.trim() === '') return
  savingRel.value = true
  try {
    await upsertEntityRelationship(props.entityId, relForm.to_entity_id.trim(), {
      address: relForm.address.trim() || null,
      notes: relForm.notes.trim() || null,
    })
    const { relationships: fresh } = await listEntityRelationships(props.entityId)
    relationships.value = fresh
    relForm.to_entity_id = ''
    relForm.address = ''
    relForm.notes = ''
    toast.success(t('visualGameAdmin.entityLibrary.detail.relationships.saved'))
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    savingRel.value = false
  }
}

// ------------------------------------------------------------------ Deep research（R3/R4）

const researching = ref(false)

async function triggerDeepResearch() {
  researching.value = true
  try {
    const result = await runEntityDeepResearch(props.entityId)
    toast.success(t('visualGameAdmin.entityLibrary.detail.deepResearch.done', { ingested: result.ingested.length, skipped: result.skipped }))
    const { assets: fresh } = await listEntityAssets(props.entityId)
    assets.value = fresh
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    researching.value = false
  }
}
</script>

<template>
  <div class="edp vga-page" data-insp-path="apps/visual-game-admin/src/pages/entity-library/EntityDetailPage.vue">
    <PageHeader :title="entity ? entity.canonical_name : entityId">
      <template #actions>
        <Button variant="ghost" size="sm" @click="back"><ArrowLeft :size="14" /> {{ t('visualGameAdmin.common.back') }}</Button>
      </template>
    </PageHeader>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else>
      <Section>
        <div class="edp__meta">
          <Badge size="sm">{{ t(`visualGameAdmin.entityLibrary.type.${entity.type}`) }}</Badge>
          <span v-if="entity.aliases && entity.aliases.length">{{ t('visualGameAdmin.entityLibrary.table.aliases') }}: {{ entity.aliases.join('、') }}</span>
          <span>{{ t('visualGameAdmin.entityLibrary.table.status') }}: {{ entity.status }}</span>
          <span>{{ t('visualGameAdmin.entityLibrary.table.source') }}: {{ entity.source }}</span>
        </div>
        <Button size="sm" :loading="researching" @click="triggerDeepResearch">
          <Sparkles :size="14" /> {{ t('visualGameAdmin.entityLibrary.detail.deepResearch.run') }}
        </Button>
        <p class="edp__hint">{{ t('visualGameAdmin.entityLibrary.detail.deepResearch.hint') }}</p>
      </Section>

      <Section :title="t('visualGameAdmin.entityLibrary.detail.factsTitle')">
        <p class="edp__hint">{{ t('visualGameAdmin.entityLibrary.detail.factsHint') }}</p>
        <Textarea v-model="hardFactsText" :rows="12" class="edp__facts-editor" />
        <p v-if="factsError" class="vga-error">{{ factsError }}</p>
        <Button size="sm" :loading="savingFacts" @click="saveHardFacts">
          <Save :size="14" /> {{ t('visualGameAdmin.common.save') }}
        </Button>
      </Section>

      <Section :title="t('visualGameAdmin.entityLibrary.detail.assets.title')" :padded="false">
        <EmptyState v-if="assets.length === 0" :title="t('visualGameAdmin.entityLibrary.detail.assets.emptyTitle')" />
        <Table v-else :columns="assetColumns" :rows="assets">
          <template #asset_type="{ row }"><Badge size="sm">{{ row.asset_type }}</Badge></template>
          <template #pack_id="{ row }">{{ row.pack_id ?? t('visualGameAdmin.entityLibrary.detail.assets.canonical') }}</template>
          <template #status="{ row }"><Badge :variant="row.status === 'withdrawn' ? 'danger' : 'default'" size="sm">{{ row.status }}</Badge></template>
          <template #ref="{ row }">
            <a v-if="row.asset_type === 'image' && row.media_public_url" :href="row.media_public_url" target="_blank" rel="noopener" class="edp__asset-ref">
              <img :src="row.media_public_url" class="edp__asset-thumb" :alt="row.role" loading="lazy" />
              <span class="edp__asset-id">{{ row.media_asset_id }}</span>
            </a>
            <span v-else>{{ row.media_asset_id ?? row.prompt_id }}</span>
          </template>
          <template #actions="{ row }">
            <Button v-if="row.status !== 'withdrawn'" variant="ghost" size="sm" :loading="withdrawingId === row.id" @click="withdraw(row)">
              <Ban :size="14" /> {{ t('visualGameAdmin.entityLibrary.detail.assets.withdraw') }}
            </Button>
          </template>
        </Table>
      </Section>
      <Section>
        <form class="edp__inline-form" @submit.prevent="addAsset">
          <FormField :label="t('visualGameAdmin.entityLibrary.detail.assets.type')">
            <Select v-model="assetForm.asset_type" :options="['image', 'video', 'audio', 'prompt'].map(v => ({ value: v, label: v }))" size="sm" />
          </FormField>
          <FormField :label="t('visualGameAdmin.entityLibrary.detail.assets.role')">
            <Input v-model="assetForm.role" size="sm" />
          </FormField>
          <FormField :label="t('visualGameAdmin.entityLibrary.detail.assets.pack')">
            <Input v-model="assetForm.pack_id" size="sm" :placeholder="t('visualGameAdmin.entityLibrary.detail.assets.canonical')" />
          </FormField>
          <FormField v-if="assetForm.asset_type === 'prompt'" :label="t('visualGameAdmin.entityLibrary.detail.assets.promptId')">
            <Input v-model="assetForm.prompt_id" size="sm" />
          </FormField>
          <FormField v-else :label="t('visualGameAdmin.entityLibrary.detail.assets.mediaAssetId')">
            <Input v-model="assetForm.media_asset_id" size="sm" />
          </FormField>
          <Button size="sm" type="submit" :loading="addingAsset" :disabled="!assetFormValid">
            <Plus :size="14" /> {{ t('visualGameAdmin.common.save') }}
          </Button>
        </form>
      </Section>

      <Section :title="t('visualGameAdmin.entityLibrary.detail.relationships.title')" :padded="false">
        <EmptyState v-if="relationships.length === 0" :title="t('visualGameAdmin.entityLibrary.detail.relationships.emptyTitle')" />
        <Table v-else :columns="relationshipColumns" :rows="relationships">
          <template #direction="{ row }"><Badge size="sm">{{ row.direction }}</Badge></template>
        </Table>
      </Section>
      <Section>
        <form class="edp__inline-form" @submit.prevent="addRelationship">
          <FormField :label="t('visualGameAdmin.entityLibrary.detail.relationships.toEntityId')">
            <Input v-model="relForm.to_entity_id" size="sm" />
          </FormField>
          <FormField :label="t('visualGameAdmin.entityLibrary.detail.relationships.address')">
            <Input v-model="relForm.address" size="sm" />
          </FormField>
          <FormField :label="t('visualGameAdmin.entityLibrary.detail.relationships.notes')">
            <Input v-model="relForm.notes" size="sm" />
          </FormField>
          <Button size="sm" type="submit" :loading="savingRel" :disabled="relForm.to_entity_id.trim() === ''">
            <Plus :size="14" /> {{ t('visualGameAdmin.common.save') }}
          </Button>
        </form>
      </Section>
    </template>
  </div>
</template>

<style scoped>
.edp__meta { display: flex; flex-wrap: wrap; gap: var(--drama-space-md); align-items: center; margin-bottom: var(--drama-space-sm); font-size: 13px; color: var(--drama-color-text-muted); }
.edp__hint { margin: var(--drama-space-xs) 0 var(--drama-space-sm); font-size: 12px; color: var(--drama-color-text-muted); }
.edp__inline-form { display: flex; gap: var(--drama-space-sm); align-items: flex-end; flex-wrap: wrap; }
.edp__facts-editor { font-family: var(--drama-font-family-mono, monospace); font-size: 12px; }
.edp__asset-ref { display: flex; align-items: center; gap: var(--drama-space-xs); color: inherit; text-decoration: none; }
.edp__asset-thumb { width: 40px; height: 40px; object-fit: cover; border-radius: var(--drama-radius-sm); border: 1px solid var(--drama-color-border); flex-shrink: 0; }
.edp__asset-id { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
